import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/config';
// Import necessary Firestore functions
import { collection, query, where, onSnapshot, getDocs, limit } from 'firebase/firestore';
import LoadingSpinner from '../../components/common/LoadingSpinner';
// Import required icons
import { ChevronDownIcon, DownloadIcon } from '../../components/icons/Icons';
// Import PDF generation libraries
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable'; // Import the function directly

// Helper function to determine the financial year (April to March)
const getFinancialYear = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth(); // 0-11
    return month >= 3 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
};

// Component to display the aggregated statement data and handle PDF download
function StatementDisplay({ reports, userProfile }) {
    const [expandedYear, setExpandedYear] = useState(getFinancialYear(new Date()));

    // Aggregate report data by financial year and month
    const aggregatedData = useMemo(() => {
        const approvedReports = reports.filter(r => r.status === 'Approved' || r.status === 'Reimbursed');
        if (approvedReports.length === 0) return [];

        const dataByYear = approvedReports.reduce((acc, report) => {
            const year = getFinancialYear(report.createdAt.toDate());
            if (!acc[year]) {
                acc[year] = [];
            }
            acc[year].push(report);
            return acc;
        }, {});

        return Object.keys(dataByYear).sort().reverse().map(year => {
            const reportsInYear = dataByYear[year];
            const months = {};
            const financialYearMonths = ["April", "May", "June", "July", "August", "September", "October", "November", "December", "January", "February", "March"];

            reportsInYear.forEach(report => {
                const monthName = report.createdAt.toDate().toLocaleString('default', { month: 'long' });
                if (!months[monthName]) {
                    months[monthName] = { INR: 0, SGD: 0 };
                }
                if (report.reimbursementCurrency === 'INR') {
                    months[monthName].INR += report.totalAmount;
                } else if (report.reimbursementCurrency === 'SGD') {
                    months[monthName].SGD += report.totalAmount;
                }
            });
            
            return {
                year,
                months,
                financialYearMonths,
            };
        });
    }, [reports]);

    // Function to generate and download the PDF
    const handleDownloadPDF = (yearData) => {
        const doc = new jsPDF();
        const companyName = "Agamlinc Edu Systems Private Limited, CIN: U80904MH2017PTC296057"; // Your company name
        const disclaimer = "This is a computer generated report and does not need signatures. Please contact india.accounts@linceducation.com";
	const downloadDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric'
        });

        // --- PDF Header ---
        doc.setFontSize(16);
        doc.text(companyName, 14, 22);
        doc.setFontSize(12);
        // **MODIFIED**: Added Financial Year below company name
        doc.text(`Financial Year ${yearData.year}`, 14, 30); 
        const employeeNameText = `Employee: ${userProfile.name}`;
        const panText = userProfile.panCardNumber ? ` (PAN: ${userProfile.panCardNumber})` : '';
        doc.text(employeeNameText + panText, 14, 36);
        doc.setFontSize(10); // Use a slightly smaller font for the date
        doc.setTextColor(100); // Make the date grey
        doc.text(`Generated on: ${downloadDate}`, 14, 42);
        doc.setTextColor(0); // Reset text color

        doc.setLineWidth(0.5);
        // --- FIX #3: Adjust the line position slightly ---
        doc.line(14, 45, 196, 45); // Moved down slightly

        // --- PDF Table ---
        const tableColumn = ["Month", "Expense Reimbursements (INR)"];
        const tableRows = [];

        yearData.financialYearMonths.forEach(month => {
            const monthData = [
                month,
                yearData.months[month]?.INR > 0 ? yearData.months[month].INR.toFixed(2) : '0.00',
                yearData.months[month]?.SGD > 0 ? yearData.months[month].SGD.toFixed(2) : '0.00',
            ];
            tableRows.push(monthData);
        });

        autoTable(doc, { // Pass the 'doc' instance as the first argument
            head: [tableColumn],
            body: tableRows,
            startY: 50,
            theme: 'grid',
            headStyles: { fillColor: [33, 51, 104] }, // #213368
            styles: { fontSize: 10 },
        });

        // --- PDF Footer (Disclaimer and Page Number) ---
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(150);
            doc.text(disclaimer, 14, doc.internal.pageSize.height - 15);
            doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 35, doc.internal.pageSize.height - 15);
        }

        // --- Save the PDF file ---
        doc.save(`ExpenseStatement_${userProfile.name}_FY${yearData.year}.pdf`);
    };

    if (aggregatedData.length === 0) {
        return <p className="text-center text-gray-500 py-8">No approved or reimbursed expenses to display.</p>;
    }

    return (
        <div className="space-y-4">
            {aggregatedData.map(data => (
                <div key={data.year} className="border border-gray-200 rounded-lg">
                    {/* Collapsible header */}
                    <div
                        className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 flex justify-between items-center"
                        onClick={() => setExpandedYear(expandedYear === data.year ? null : data.year)}
                    >
                        <h3 className="text-lg font-semibold text-[#213368]">Financial Year: {data.year}</h3>
                        <ChevronDownIcon className={`h-6 w-6 transition-transform ${expandedYear === data.year ? 'rotate-180' : ''}`} />
                    </div>
                    {/* Expanded content */}
                    {expandedYear === data.year && (
                        <div className="p-4">
                            {/* Download Button */}
                            <div className="flex justify-end mb-4">
                                <button
                                    onClick={() => handleDownloadPDF(data)}
                                    className="flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                                >
                                    <DownloadIcon />
                                    <span className="ml-2">Download PDF</span>
                                </button>
                            </div>
                            {/* Statement Table */}
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="px-4 py-2 text-left font-medium">Month</th>
                                            <th className="px-4 py-2 text-right font-medium">Reimbursed (INR)</th>
                                            <th className="px-4 py-2 text-right font-medium">Reimbursed (SGD)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.financialYearMonths.map(month => (
                                            <tr key={month} className="border-t">
                                                <td className="px-4 py-2">{month}</td>
                                                <td className="px-4 py-2 text-right text-gray-700">
                                                    {data.months[month]?.INR > 0 ? data.months[month].INR.toFixed(2) : '0.00'}
                                                </td>
                                                <td className="px-4 py-2 text-right text-gray-700">
                                                    {data.months[month]?.SGD > 0 ? data.months[month].SGD.toFixed(2) : '0.00'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

// Sub-component for the "My Statement" tab
function MyStatementView({ userProfile, appId }) {
    const [myReports, setMyReports] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const reportsQuery = query(
            collection(db, `/artifacts/${appId}/public/data/expenseReports`),
            where("requesterId", "==", userProfile.uid)
        );
        const unsub = onSnapshot(reportsQuery, (snapshot) => {
            setMyReports(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setIsLoading(false);
        });
        return () => unsub();
    }, [appId, userProfile.uid]);

    if (isLoading) return <LoadingSpinner />;

    // Pass reports and userProfile for PDF generation
    return <StatementDisplay reports={myReports} userProfile={userProfile} />; 
}

// Sub-component for the "Team Statements" tab
function TeamStatementView({ userProfile, appId }) {
    const [teamMembers, setTeamMembers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedUserReports, setSelectedUserReports] = useState([]);
    const [isLoadingMembers, setIsLoadingMembers] = useState(true);
    const [isLoadingReports, setIsLoadingReports] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null); // Store the whole member object

    useEffect(() => {
        const usersRef = collection(db, `/artifacts/${appId}/users`);
        let q;
        if (userProfile.role === 'Admin' || userProfile.role === 'HR') {
            q = query(usersRef, where("status", "==", "Approved"));
        } else {
            q = query(usersRef, where("managerId", "==", userProfile.uid), where("status", "==", "Approved"));
        }
        
        const unsub = onSnapshot(q, (snapshot) => {
            setTeamMembers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoadingMembers(false);
        });
        return () => unsub();
    }, [appId, userProfile]);

    useEffect(() => {
        if (!selectedUserId) {
            setSelectedUserReports([]);
            return;
        }
        setIsLoadingReports(true);
        const reportsQuery = query(
            collection(db, `/artifacts/${appId}/public/data/expenseReports`),
            where("requesterId", "==", selectedUserId)
        );
        const unsub = onSnapshot(reportsQuery, (snapshot) => {
            setSelectedUserReports(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setIsLoadingReports(false);
        });
        return () => unsub();
    }, [selectedUserId, appId]);

    const filteredMembers = useMemo(() => {
        return teamMembers.filter(member => member.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [teamMembers, searchTerm]);

    const handleSelectMember = (member) => {
        setSelectedUserId(member.id);
        setSelectedMember(member); // Store selected member's profile
        setSearchTerm(''); 
        setIsDropdownOpen(false);
    };

    if (isLoadingMembers) return <LoadingSpinner />;

    return (
        <div>
            <div className="mb-6 max-w-sm relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Employee</label>
                <div className="relative">
                    <button 
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)} 
                        className="w-full p-2 border border-gray-300 rounded-md shadow-sm text-left bg-white flex justify-between items-center"
                    >
                        {selectedMember ? selectedMember.name : "Select a team member"}
                        <ChevronDownIcon className={`h-5 w-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                            <input
                                type="text"
                                placeholder="Search members..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full p-2 border-b"
                            />
                            <ul className="max-h-60 overflow-y-auto">
                                {filteredMembers.length > 0 ? (
                                    filteredMembers.map(member => (
                                        <li 
                                            key={member.id} 
                                            onClick={() => handleSelectMember(member)} 
                                            className="p-2 hover:bg-gray-100 cursor-pointer"
                                        >
                                            {member.name}
                                        </li>
                                    ))
                                ) : (
                                    <li className="p-2 text-gray-500">No members found.</li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {/* Pass the selected member's profile to StatementDisplay for PDF generation */}
            {selectedUserId && ( 
                isLoadingReports ? <LoadingSpinner /> : <StatementDisplay reports={selectedUserReports} userProfile={selectedMember} /> 
            )}
        </div>
    );
}

// Main component for the "Expense Statements" view
export default function ExpenseStatements({ userProfile, appId }) {
    const [activeTab, setActiveTab] = useState('myStatement');
    const [isActingManager, setIsActingManager] = useState(false);
    const [isLoadingManagerCheck, setIsLoadingManagerCheck] = useState(true);

    useEffect(() => {
        if (!userProfile || userProfile.role === 'Admin' || userProfile.role === 'HR') {
            setIsLoadingManagerCheck(false);
            return;
        }

        const checkManagerStatus = async () => {
            setIsLoadingManagerCheck(true);
            const usersRef = collection(db, `/artifacts/${appId}/users`);
            const q = query(usersRef, where("managerId", "==", userProfile.uid), limit(1)); 
            
            try {
                const snapshot = await getDocs(q);
                setIsActingManager(!snapshot.empty); 
            } catch (error) {
                console.error("Error checking manager status:", error);
                setIsActingManager(false);
            } finally {
                setIsLoadingManagerCheck(false);
            }
        };

        checkManagerStatus();

    }, [appId, userProfile]);

    const canViewTeamStatements = useMemo(() => {
        if (!userProfile) return false;
        return ['Admin', 'HR'].includes(userProfile.role) || isActingManager;
    }, [userProfile, isActingManager]);

    if (isLoadingManagerCheck) {
        return <LoadingSpinner />;
    }

    return (
        <div className="bg-white p-8 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold text-[#213368] mb-4">Expense Statements</h2>
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    <button
                        onClick={() => setActiveTab('myStatement')}
                        className={`${activeTab === 'myStatement' ? 'border-[#213368] text-[#213368]' : 'border-transparent text-gray-500'} py-4 px-1 border-b-2 font-medium text-sm`}
                    >
                        My Statement
                    </button>
                    {canViewTeamStatements && (
                        <button
                            onClick={() => setActiveTab('team')}
                            className={`${activeTab === 'team' ? 'border-[#213368] text-[#213368]' : 'border-transparent text-gray-500'} py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            Team Statements
                        </button>
                    )}
                </nav>
            </div>
            <div className="pt-6">
                {activeTab === 'myStatement' && <MyStatementView userProfile={userProfile} appId={appId} />}
                {activeTab === 'team' && canViewTeamStatements && <TeamStatementView userProfile={userProfile} appId={appId} />}
            </div>
        </div>
    );
}